import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Loader2 } from 'lucide-react';
import { logPropertyQuote } from '@/services/quotes.service.js';

// PROPERTY-LINE-SPEC P2 — broker "Log market quote" form for a builders risk
// deal. One Quotation row per market response: a single builders_risk coverage
// line carries the terms (limit/deductible vocabulary mirrors the
// Builders_Risk_Data blob coverage section, so the P4a select-market merge is
// a key-for-key copy) plus an optional tria line for the premium + TRIA split.
// The blob coverage is the APPLICATION ask; this sheet records the MARKET's
// answer. Never presents, never touches Tower state (presentation is P4).
//
// Mirrors LogHangarQuoteSheet structure (shadcn Sheet, toast, onSaved).
// No em dashes anywhere (CLAUDE.md hard rule).

function num(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// Deductibles accept "$5,000" or "5%": percent strings pass through verbatim,
// everything else normalizes to a number.
function ded(v) {
  const s = String(v ?? '').trim();
  if (!s) return null;
  if (s.includes('%')) return s;
  return num(s);
}

function seed() {
  return {
    carrier: '',
    quote_number: '',
    effective_date: '',
    expiration_date: '',
    premium: '',
    tria_premium: '',
    any_jobsite_limit: '',
    any_jobsite_deductible: '',
    any_one_loss_limit: '',
    any_one_loss_deductible: '',
    transit_limit: '',
    transit_deductible: '',
    temp_storage_limit: '',
    temp_storage_deductible: '',
    earthquake_limit: '',
    flood_limit: '',
    windstorm_deductible: '',
    coinsurance: '',
    permission_to_occupy: '',
    special_form: false,
    notes: '',
    subject_to: '',
  };
}

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-[11px] text-text-dim">{label}</span>
    {children}
  </label>
);

export default function LogPropertyQuoteSheet({ dealId, open, onOpenChange, onSaved }) {
  const [f, setF] = useState(seed);
  const [saving, setSaving] = useState(false);

  // Reset on each open so a cancel discards edits.
  useEffect(() => { if (open) setF(seed()); }, [open]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const canSave = !!f.carrier.trim() && !!f.effective_date && num(f.premium) != null && !saving;
  const total = (num(f.premium) || 0) + (num(f.tria_premium) || 0);

  function buildLines() {
    const limits = {};
    const deductibles = {};
    if (num(f.any_jobsite_limit) != null) limits.any_jobsite = num(f.any_jobsite_limit);
    if (num(f.any_one_loss_limit) != null) limits.any_one_loss = num(f.any_one_loss_limit);
    if (num(f.transit_limit) != null) limits.transit = num(f.transit_limit);
    if (num(f.temp_storage_limit) != null) limits.temp_storage = num(f.temp_storage_limit);
    if (num(f.earthquake_limit) != null) limits.earthquake = num(f.earthquake_limit);
    if (num(f.flood_limit) != null) limits.flood = num(f.flood_limit);
    if (ded(f.any_jobsite_deductible) != null) deductibles.any_jobsite = ded(f.any_jobsite_deductible);
    if (ded(f.any_one_loss_deductible) != null) deductibles.any_one_loss = ded(f.any_one_loss_deductible);
    if (ded(f.transit_deductible) != null) deductibles.transit = ded(f.transit_deductible);
    if (ded(f.temp_storage_deductible) != null) deductibles.temp_storage = ded(f.temp_storage_deductible);
    if (ded(f.windstorm_deductible) != null) deductibles.windstorm = ded(f.windstorm_deductible);

    const payload = {};
    if (f.coinsurance.trim()) payload.coinsurance = f.coinsurance.trim();
    if (f.permission_to_occupy) payload.permission_to_occupy = f.permission_to_occupy;
    if (f.special_form) payload.special_form = true;
    if (f.notes.trim()) payload.notes = f.notes.trim();

    const lines = [
      { line_type: 'builders_risk', subject_kind: null, premium: num(f.premium), limits, deductibles, payload },
    ];
    if (num(f.tria_premium) != null) {
      lines.push({ line_type: 'tria', subject_kind: null, premium: num(f.tria_premium) });
    }
    return lines;
  }

  async function handleSave() {
    if (!dealId || !canSave) return;
    setSaving(true);
    try {
      const res = await logPropertyQuote({
        deal_id: dealId,
        carrier: f.carrier.trim(),
        quote_number: f.quote_number.trim() || null,
        effective_date: f.effective_date,
        expiration_date: f.expiration_date || null,
        subject_to: f.subject_to.trim() || null,
        lines: buildLines(),
      });
      if (!res?.success) { toast.error(res?.error || 'Save failed'); return; }
      toast.success('Market quote logged');
      onSaved?.(res);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.body?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Log market quote</SheetTitle>
          <SheetDescription>
            Enter a market&apos;s builders risk response. One entry per market; the application ask stays in the project data.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 py-4">
          {/* Market + reference */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Carrier / market *">
              <Input value={f.carrier} onChange={(e) => set('carrier', e.target.value)} placeholder="e.g. US Assure (Zurich)" />
            </Field>
            <Field label="Quote number">
              <Input value={f.quote_number} onChange={(e) => set('quote_number', e.target.value)} placeholder="Carrier quote ref" />
            </Field>
            <Field label="Term effective *">
              <Input type="date" value={f.effective_date} onChange={(e) => set('effective_date', e.target.value)} />
            </Field>
            <Field label="Term expiration">
              <Input type="date" value={f.expiration_date} onChange={(e) => set('expiration_date', e.target.value)} />
            </Field>
          </div>

          {/* Premium split */}
          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Premium</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Premium (pre-tax, pre-TRIA) *">
                <Input value={f.premium} onChange={(e) => set('premium', e.target.value)} placeholder="$" />
              </Field>
              <Field label="TRIA premium">
                <Input value={f.tria_premium} onChange={(e) => set('tria_premium', e.target.value)} placeholder="$" />
              </Field>
            </div>
          </div>

          {/* Limits + deductibles */}
          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Limits and deductibles</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Any one jobsite limit">
                <Input value={f.any_jobsite_limit} onChange={(e) => set('any_jobsite_limit', e.target.value)} placeholder="$" />
              </Field>
              <Field label="Jobsite deductible">
                <Input value={f.any_jobsite_deductible} onChange={(e) => set('any_jobsite_deductible', e.target.value)} placeholder="$ or %" />
              </Field>
              <Field label="Any one loss limit">
                <Input value={f.any_one_loss_limit} onChange={(e) => set('any_one_loss_limit', e.target.value)} placeholder="$" />
              </Field>
              <Field label="Any one loss deductible">
                <Input value={f.any_one_loss_deductible} onChange={(e) => set('any_one_loss_deductible', e.target.value)} placeholder="$ or %" />
              </Field>
              <Field label="Transit limit">
                <Input value={f.transit_limit} onChange={(e) => set('transit_limit', e.target.value)} placeholder="$" />
              </Field>
              <Field label="Transit deductible">
                <Input value={f.transit_deductible} onChange={(e) => set('transit_deductible', e.target.value)} placeholder="$ or %" />
              </Field>
              <Field label="Temp storage limit">
                <Input value={f.temp_storage_limit} onChange={(e) => set('temp_storage_limit', e.target.value)} placeholder="$" />
              </Field>
              <Field label="Temp storage deductible">
                <Input value={f.temp_storage_deductible} onChange={(e) => set('temp_storage_deductible', e.target.value)} placeholder="$ or %" />
              </Field>
              <Field label="Earthquake limit">
                <Input value={f.earthquake_limit} onChange={(e) => set('earthquake_limit', e.target.value)} placeholder="$" />
              </Field>
              <Field label="Flood limit">
                <Input value={f.flood_limit} onChange={(e) => set('flood_limit', e.target.value)} placeholder="$" />
              </Field>
              <Field label="Wind / hail deductible">
                <Input value={f.windstorm_deductible} onChange={(e) => set('windstorm_deductible', e.target.value)} placeholder="$ or %" />
              </Field>
              <Field label="Coinsurance">
                <Input value={f.coinsurance} onChange={(e) => set('coinsurance', e.target.value)} placeholder="e.g. waived / 80%" />
              </Field>
            </div>
          </div>

          {/* Form + occupancy flags */}
          <div className="space-y-2">
            <Field label="Permission to occupy">
              <div className="flex gap-2">
                {[['', 'Not stated'], ['Granted', 'Granted'], ['Not Granted', 'Not granted']].map(([val, label]) => (
                  <Button key={val || 'none'} type="button" size="sm" variant={f.permission_to_occupy === val ? 'default' : 'outline'} onClick={() => set('permission_to_occupy', val)}>
                    {label}
                  </Button>
                ))}
              </div>
            </Field>
            <label className="flex items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={f.special_form} onChange={(e) => set('special_form', e.target.checked)} />
              Special form (all risk) cause of loss
            </label>
            <Field label="Coverage notes">
              <Input value={f.notes} onChange={(e) => set('notes', e.target.value)} placeholder="e.g. theft included; scaffolding excluded" />
            </Field>
          </div>

          {/* Subjectivities (broker-internal) */}
          <Field label="Subjectivities (broker-internal, never client-facing)">
            <Input value={f.subject_to} onChange={(e) => set('subject_to', e.target.value)} placeholder="e.g. signed application; builders risk questionnaire" />
          </Field>

          {/* Total preview */}
          <div className="rounded-card border border-border p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Total (premium + TRIA)</span>
              <span className="font-semibold text-text">${total.toLocaleString('en-US')}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? (<><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Saving…</>) : 'Log quote'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
